import { RELAY_SIGNALS, type RelaySignal, type RelayStrategy } from './relay'

interface ShortPollingOptions {
  intervalMs: number
  maxIntervalMs: number
  cadence?: {
    allChangedEveryTicks: number
    intermediateSignal: RelaySignal
    shouldPublishIntermediate: () => boolean
  }
}

export const createShortPollingStrategy = ({
  intervalMs,
  maxIntervalMs,
  cadence,
}: ShortPollingOptions): RelayStrategy => {
  if (
    intervalMs <= 0 ||
    maxIntervalMs < intervalMs ||
    (cadence &&
      (!Number.isInteger(cadence.allChangedEveryTicks) ||
        cadence.allChangedEveryTicks < 2))
  ) {
    throw new Error('Invalid Relay polling interval')
  }

  return {
    start: (publish) => {
      let stopped = false
      let broadFailures = 0
      let intermediateFailures = 0
      let ticks = 0
      let timer: ReturnType<typeof setTimeout> | undefined

      const tick = async (): Promise<void> => {
        ticks += 1
        const broadRefresh =
          !cadence || ticks % cadence.allChangedEveryTicks === 0
        if (broadRefresh) {
          try {
            await publish(RELAY_SIGNALS.allChanged)
            broadFailures = 0
            intermediateFailures = 0
          } catch {
            broadFailures += 1
          }
        } else {
          try {
            if (cadence.shouldPublishIntermediate()) {
              await publish(cadence.intermediateSignal)
              intermediateFailures = 0
            } else {
              intermediateFailures = 0
            }
          } catch {
            intermediateFailures += 1
          }
        }
        if (stopped) return
        const failures = Math.max(broadFailures, intermediateFailures)
        const delay = Math.min(intervalMs * 2 ** failures, maxIntervalMs)
        timer = setTimeout(tick, delay)
      }

      timer = setTimeout(tick, intervalMs)
      return () => {
        stopped = true
        clearTimeout(timer)
      }
    },
  }
}
